import { useEffect, useRef, useState } from 'react';
import { defaultSelection } from '@/lib/sources/scopeSelection';
import type { OutlineSection } from '@/types/api';

/**
 * Which parts are ticked. Starts with the parts that fit `budget`, and re-fits
 * when the budget arrives late (the month's allowance loads after the dialog
 * opens) - until the student changes anything themselves.
 */
export function usePartSelection(
  sections: OutlineSection[],
  budget: number | null
) {
  const [selected, setSelected] = useState<Set<string>>(() =>
    defaultSelection(sections, budget)
  );
  const touched = useRef(false);
  useEffect(() => {
    if (!touched.current) setSelected(defaultSelection(sections, budget));
  }, [sections, budget]);

  const toggle = (id: string) => {
    touched.current = true;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setAll = (ids: string[], on: boolean) => {
    touched.current = true;
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const markTouched = () => {
    touched.current = true;
  };

  return { selected, toggle, setAll, markTouched };
}
